// ─────────────────────────────────────────────────────────────────────────────
// useDreamReview — GET /api/rexy/dream-review + POST /api/rexy/dream-review/decision.
// Shared core (owner ruling 2026-07-31, "one core, every window").
//
// THE OWNER DREAM GATE (owner rulings G1–G12, 2026-10-20). What her nights dream —
// learnings and his preferences — waits here as CANDIDATES until he confirms or
// rejects each one; only what he confirms (a KEY MOMENT) ever reaches her. A
// candidate is shown VERBATIM with when it was formed, the days it rests on and
// what it rests on. `decide()` is a GATE, not a render concern: it sends the exact
// revision he was shown, and a decision is shown as made only once the server says
// so — a refusal (changed since, decided the other way) reloads the list.
//
// DESKTOP ONLY (owner ruling G12). Its two routes live here, not in endpoints.js:
// that file is copied byte-for-byte into the phone surface the iOS app loads
// (scripts/copyPhoneCore.js), and the phone must not change. They stand on her
// one API base, read off DREAMSTATE's.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { authHeaders, POLL_MS } from './authHeaders';
import { DREAMSTATE } from './endpoints';

const API = DREAMSTATE.slice(0, DREAMSTATE.lastIndexOf('/'));

/** GET — what her nights dreamed and wait on him (the owner dream gate), and her key moments. */
export const DREAM_REVIEW = `${API}/dream-review`;

/** POST — his decision on one candidate: { id, revision, decision: 'confirm' | 'reject' }. */
export const DREAM_REVIEW_DECISION = `${API}/dream-review/decision`;

/** A value the server names, looked up as an OWN key only ("toString" is not a label). */
export function own(table: Record<string, string>, key: unknown): string | undefined {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key)
    ? table[key] : undefined;
}

/** What a candidate rests on, in the night's own labels. */
export const REST_LABEL: Record<string, string> = {
  his_words: 'his words',
  her_replies: 'her replies',
  notes: 'notes',
  reports: 'reports',
  other: 'other',
};

export type DreamCandidate = {
  id: string;
  revision: string;                 // the exact text he is shown — sent back with his decision
  kind: 'learning' | 'preference';
  topic: string;
  text: string;                     // what her night wrote, VERBATIM
  formed_at: string;
  formed_label: string;             // "Tue 20 Oct 2026 02:30" — her day-label words
  days: string[];
  days_label: string;               // the days it rests on, "Fri 16, Mon 19 Oct 2026"
  rests_on: Record<string, number>; // his_words / her_replies / notes / reports
  seen: number | null;
  about: string[] | null;
  his_words: string[];
  replaces: { id: string; text: string; confirmed_at: string } | null;
};

export type DreamReviewGroup = {
  run_id: string | null;
  night: string | null;             // the day the night covered
  formed_at: string;                // when that night ran
  formed_label: string;
  candidates: DreamCandidate[];
};

export type DreamKeyMoment = {
  id: string;
  kind: 'learning' | 'preference';
  topic: string;
  text: string;
  confirmed_at: string;
  confirmed_label: string;
  last_supported: string;
  last_supported_label: string;
};

export type DreamReviewView = {
  groups: DreamReviewGroup[];       // newest night first; no limit, no expiry
  pending_total: number;
  key_moments: DreamKeyMoment[];
  store_status: string;
};

export type DreamDecision = 'confirm' | 'reject';

/** The server's answer to his last decision — a few words, never the reasoning. */
export type DreamNotice = { kind: 'done' | 'refused'; text: string; topic: string };

export type DreamReviewFeed = {
  view: DreamReviewView | null;
  err: string | null;
  deciding: string | null;
  notice: DreamNotice | null;
  decide: (candidate: DreamCandidate, decision: DreamDecision) => void;
  refetch: () => void;
};

const DONE: Record<string, string> = {
  confirmed: 'Confirmed.',
  rejected: 'Rejected.',
  already: 'Already decided.',
};
const REFUSED: Record<string, string> = {
  stale: 'Changed since — reloaded.',
  conflict: 'Decided the other way.',
  unknown: 'No longer waiting.',
  invalid: 'Not sent.',
};

export function useDreamReview(): DreamReviewFeed {
  const [view, setView] = useState<DreamReviewView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [notice, setNotice] = useState<DreamNotice | null>(null);

  const fetchReview = useCallback(() => {
    fetch(DREAM_REVIEW, { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success && j.review) {
          setView(j.review as DreamReviewView);
          setErr(null);
        } else setErr((j && j.error) || 'unavailable');
      })
      .catch((e) => setErr(String(e)));
  }, []);

  // held in a ref so decide's identity stays stable for consumers that memoise on it
  const fetchRef = useRef(fetchReview);
  fetchRef.current = fetchReview;

  useEffect(() => {
    fetchReview();
    const poll = setInterval(fetchReview, POLL_MS);
    return () => clearInterval(poll);
  }, [fetchReview]);

  const decide = useCallback((candidate: DreamCandidate, decision: DreamDecision) => {
    setDeciding(candidate.id);
    setNotice(null);
    fetch(DREAM_REVIEW_DECISION, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ id: candidate.id, revision: candidate.revision, decision }),
    })
      .then((r) => r.json().then((j) => ({ status: r.status, j }), () => ({ status: r.status, j: null })))
      .then(({ status, j }) => {
        const result = j && j.result;
        const topic = candidate.topic;
        const done = result && own(DONE, result.status);
        if (j && j.success === true && status === 200 && result && result.id === candidate.id && done) {
          // shown as made only now: the server has written it
          setNotice({ kind: 'done', text: done, topic });
        } else if (result && result.status === 'refused') {
          setNotice({ kind: 'refused', text: own(REFUSED, result.code) || 'Not sent.', topic });
        } else {
          setNotice({ kind: 'refused', text: status === 401 ? 'Not authorised.' : 'Not sent.', topic });
        }
        fetchRef.current();
      })
      .catch(() => setNotice({ kind: 'refused', text: 'Not sent.', topic: candidate.topic }))
      .finally(() => setDeciding(null));
  }, []);

  return { view, err, deciding, notice, decide, refetch: fetchReview };
}
